import React, { type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import { FluxoPagamentoSheet } from '@/features/simulacao/components/fluxo-pagamento'
import { useSimulacaoContentContext } from '@/features/simulacao/context/simulacao-content.context'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { SimulacaoParcelasAgrupadas } from '@/shared/components/simulacao-agrupador-parcelas'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionTitle,
  SectionItemValue,
} from '@/shared/components/ui'
import { formatToBRL } from '@/shared/utils/currency.utils'

type SectionItemParcelaProps<T extends { valor: number }> = {
  label: string
  parcela?: T
  colSpan?: 4 | 8 | 12
  withRightBorder?: boolean
  className?: string
}

function SectionItemParcela<T extends { valor: number }>({
  label,
  parcela,
  colSpan,
  withRightBorder,
  className,
}: SectionItemParcelaProps<T>) {
  return (
    <SectionItem label={label} colSpan={colSpan} withRightBorder={withRightBorder} className={className}>
      <Render.If condition={parcela} fallback={<SectionItemValue>{formatToBRL(0)}</SectionItemValue>}>
        {(p) => <SectionItemValue>{formatToBRL(p.valor)}</SectionItemValue>}
      </Render.If>
    </SectionItem>
  )
}

type SectionItemGrupoParcelaProps<T extends Array<{ valor: number; quantidade: number }>> = {
  label: string
  parcelas?: T
  colSpan?: 4 | 8 | 12
  withRightBorder?: boolean
  className?: string
}

function SectionItemGrupoParcela<T extends Array<{ valor: number; quantidade: number }>>({
  label,
  parcelas,
  colSpan,
  withRightBorder,
  className,
}: Readonly<SectionItemGrupoParcelaProps<T>>) {
  const { t } = useTranslation('simulacao')

  return (
    <SectionItem label={label} colSpan={colSpan} withRightBorder={withRightBorder} className={className}>
      <Render.If
        condition={parcelas}
        fallback={
          <SectionItemValue uppercase>{t('pages.simulacao.valores_simulados.nenhuma_parcela')}</SectionItemValue>
        }
      >
        {(p) => (
          <SimulacaoParcelasAgrupadas
            parcelas={p}
            isGrupoSaldo={false}
            className="font-poppins text-base font-normal"
          />
        )}
      </Render.If>
    </SectionItem>
  )
}

const ValoresSimuladosWrapper = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('simulacao')
  const { fluxoPagamento } = useSimulacaoContentContext()

  return (
    <React.Fragment>
      <SectionHeader>
        <SectionTitle>{t('pages.simulacao.valores_simulados.titulo')}</SectionTitle>
        <span className="hidden sm:block">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados}>
                <Button variant="outline" className="h-8 bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </SectionHeader>
      <SectionContent className="pb-16 sm:pb-0">
        {children}

        <span className="block sm:hidden">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados ?? []}>
                <Button variant="outline" className="text-primary h-12 w-full bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </SectionContent>
    </React.Fragment>
  )
}

const ValoresSimuladosContentVertical = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  return (
    <React.Fragment>
      <SectionGroup withBorder>
        <SectionItem label={t('pages.simulacao.valores_simulados.valor_negociado')}>
          <SectionItemValue>{formatToBRL(simulacao.valorTotalSimulado)}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('pages.simulacao.valores_simulados.poupanca')} colSpan={4} withRightBorder>
          <SectionItemValue>{formatToBRL(simulacao.valorPoupanca)}</SectionItemValue>
        </SectionItem>

        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.entrada')}
          parcelas={simulacao.entrada}
          colSpan={8}
        />
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.mensais')}
          parcelas={simulacao.mensais}
          colSpan={4}
          withRightBorder
        />
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.intermediarias')}
          parcelas={simulacao.intermediarias}
          colSpan={8}
        />
      </SectionGroup>

      <SectionGroup>
        <SectionItemParcela
          label={t('pages.simulacao.valores_simulados.saldo')}
          parcela={simulacao.saldo}
          colSpan={4}
          withRightBorder
        />
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.pos_conclusao')}
          parcelas={simulacao.posConclusao}
          colSpan={8}
        />
      </SectionGroup>
    </React.Fragment>
  )
}

const ValoresSimuladosContentSacocVParcelado = () => {
  const { t } = useTranslation('simulacao')
  const { simulacao } = useSimulacaoContentContext()

  return (
    <React.Fragment>
      <SectionGroup withBorder>
        <SectionItem label={t('pages.simulacao.valores_simulados.valor_negociado')}>
          <SectionItemValue>{formatToBRL(simulacao.valorTotalSimulado)}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('pages.simulacao.valores_simulados.poupanca')} colSpan={4} withRightBorder>
          <SectionItemValue>{formatToBRL(simulacao.valorPoupanca)}</SectionItemValue>
        </SectionItem>

        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.entrada')}
          parcelas={simulacao.entrada}
          colSpan={8}
        />
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.mensais')}
          parcelas={simulacao.mensais}
          colSpan={4}
          withRightBorder
        />
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.intermediarias')}
          parcelas={simulacao.intermediarias}
          colSpan={8}
        />
      </SectionGroup>

      <SectionGroup>
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.pos_conclusao')}
          parcelas={simulacao.posConclusao}
          colSpan={4}
          withRightBorder
        />
        <SectionItemGrupoParcela
          label={t('pages.simulacao.valores_simulados.intermediarias_pos_conclusao')}
          parcelas={simulacao.intermediariasPosConclusao}
          colSpan={8}
        />
      </SectionGroup>
    </React.Fragment>
  )
}

const ValoresSimuladosSectionVertical = () => (
  <ValoresSimuladosWrapper>
    <ValoresSimuladosContentVertical />
  </ValoresSimuladosWrapper>
)

const ValoresSimuladosSectionSacocVParcelado = () => (
  <ValoresSimuladosWrapper>
    <ValoresSimuladosContentSacocVParcelado />
  </ValoresSimuladosWrapper>
)

export const ValoresSimuladosSection = () => {
  const { simulacao } = useSimulacaoContentContext()

  if (simulacao.tipoDeAmortizacao === 'SacocV') {
    if (simulacao.formaDePagamento === 'Parcelado') return <ValoresSimuladosSectionSacocVParcelado />
  }

  return <ValoresSimuladosSectionVertical />
}
